import Bowser from 'bowser';

import { authScaAPI } from 'src/core/api/axios';

type PostScaProcedureResult =
  | {
      outcome: 'success';
      type: 'mfa' | 'sca' | 'totp';
      channel?: 'email' | 'phone';
    }
  | {
      outcome: 'error';
      code:
        | 'failedWithSca'
        | 'failedWithMfa'
        | 'failedWithTotp'
        | 'failedWithRecovery'
        | 'unknown';
    };

const reshapeDevicePlatform = (detectedPlatform: string) => {
  switch (detectedPlatform) {
    case 'Android':
      return 'android';
    case 'iOS':
      return 'iOS';
    default:
      return 'desktop';
  }
};

const extractDeviceInfo = () => {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const osName = browser.getOSName();
  const browserName = browser.getBrowserName();
  if (osName && browserName) {
    return {
      name: `${osName} ${browserName}`,
      platform: reshapeDevicePlatform(osName),
    };
  }

  // We know those navigator properties (vendor/platform) are flagged as deprecated.
  if (navigator.platform && navigator.vendor) {
    return {
      name: `${navigator.platform} ${navigator.vendor}`,
      platform: reshapeDevicePlatform(navigator.platform),
    };
  }

  return {
    name: 'Unknown Device',
    platform: 'desktop',
  };
};

export const postScaProcedure = async (
  scaToken: string,
  {
    authenticationMethod,
  }: {
    authenticationMethod: 'sca' | 'mfa' | 'totp' | 'recovery';
  },
): Promise<PostScaProcedureResult> => {
  try {
    const { platform, name } = extractDeviceInfo();
    const response = await authScaAPI.post(
      '/sca/procedures',
      {
        authenticationMethod,
        trustedDevice: {
          name,
          type: 'browser',
          platform,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${scaToken}`,
        },
      },
    );
    return {
      outcome: 'success',
      type: response.data.type,
      channel: response.data.channel,
    };
  } catch (error) {
    if (
      [
        'failedWithSca',
        'failedWithMfa',
        'failedWithTotp',
        'failedWithRecovery',
      ].includes(error?.response?.data?.code)
    ) {
      return {
        outcome: 'error',
        code: error?.response?.data?.code,
      };
    }

    return {
      outcome: 'error',
      code: 'unknown',
    };
  }
};
